
import { EventEmitter } from "events"

import Promise from "plugins/bluebird/index"
import $ from "jquery"

import Modal from "app/modal/show-modal"
import Auth_action from "app/authentication/action"
import Auth_callback_queue from "app/authentication/callback_queue"

import {
  refreshAndUpdateComponentPromise,
} from "app/authentication/remote_state"

import {
  captureMessage,
  captureException,
} from "app/error_reporting/index"

import {
  getContactInfoData,
  setContactInfoData,
} from "app/local_storages/contact_info_storage/index"

import {
  create as create_user,
  ResultTypeEnums as CreateUserResultTypes,
} from "app/resources/users/create/index"

import {
  isPresentString,
} from "app/utils/type_checking"

import {
  isSignedIn,
} from "app/authentication/state"

import {
  subscribe as subscribe_country_user_following,
  ResultTypeEnums as SubscribeCountryUFollowingResultTypes,
  FollowTypes as SubscribeCountryUFollowingFollowTypes,
} from "app/resources/spacious_country_user_follow_relationships/subscribe/index"

import {
  unsubscribe as unsubscribe_country_user_following,
  ResultTypeEnums as UnsubscribeCountryUFollowingResultTypes,
  FollowTypes as UnsubscribeCountryUFollowingFollowTypes,
} from "app/resources/spacious_country_user_follow_relationships/unsubscribe/index"

import {
  trackSignUp,
} from "app/authentication/tracking"

import {
  getLastContactInfoSyncPromise,
} from "app/authentication/contact_info_caching"


const RejectionReasons = {
  ERROR_REPORTED: "ERROR_REPORTED",
}

const SOURCE_FILE_PATH = "concepts/buildings/building_follow_button/factory"


interface InputSelector {
  container: Element | JQuery,

  follow_button_primary?: string,
  unfollow_button_primary?: string,
}

interface Selector {
  container: Element | JQuery,
  all_containers_on_page: string | JQuery,

  follow_button_primary: string,
  unfollow_button_primary: string,
}

const default_selector = {
  container:              $(),
  all_containers_on_page: ".cell--countries--follow-button",

  follow_button_primary:    ".cell--countries--follow-button__link--follow",
  unfollow_button_primary:  ".cell--countries--follow-button__link--unfollow",
}

export class FollowButton extends EventEmitter {
  private selector: Selector

  private readonly $container: JQuery
  private country_name_in_en: any

  private readonly $follow_button_primary: JQuery
  private readonly $unfollow_button_primary: JQuery
  private readonly on_followed_callbacks: Array<() => void> = []

  constructor(selector: InputSelector) {
    super()

    this.selector = Object.assign({}, default_selector, selector)

    this.$container = $(this.selector.container)

    this.country_name_in_en = this.$container.data("country-name-in-en")

    this.$follow_button_primary =
      this.$container.find(this.selector.follow_button_primary)
    this.$unfollow_button_primary =
      this.$container.find(this.selector.unfollow_button_primary)

    if (this.$container.length !== 1){
      captureMessage(
        `$container contains non 1 (${this.$container.length}) elements`,
        {
          source_file_path: SOURCE_FILE_PATH,
        }
      )
      return
    }

    this.bind_action()
  }

  public onFollowed(cb: () => void) {
    this.on_followed_callbacks.push(cb)
  }

  private runOnFollowedCallbacks() {
    this.on_followed_callbacks.forEach((cb) => cb())
  }

  private bind_action(){
    if (this.$follow_button_primary == null) { return }
    if (this.$unfollow_button_primary == null) { return }

    const $follow_button_primary = this.$follow_button_primary
    const $unfollow_button_primary = this.$unfollow_button_primary

    $follow_button_primary
    .on("click", (e) => {
      e.preventDefault()

      const $nearest_container = this.$container
      const follow_type: string = $nearest_container.data("follow-type")

      try {
        $follow_button_primary.prop("disabled", true)
        this.onFollowClick(follow_type)
      }
      finally {
        $follow_button_primary.prop("disabled", false)
      }
    })

    $unfollow_button_primary
    .on("click", (e) => {
      e.preventDefault()

      const $nearest_container = this.$container
      const follow_type: string = $nearest_container.data("follow-type")

      try {
        $unfollow_button_primary.prop("disabled", true)
        this.onUnfollowClick(follow_type)
      }
      finally {
        $unfollow_button_primary.prop("disabled", false)
      }
    })

    return this
  }

  private onFollowClick(follow_type: string) {
    Modal.showLoadingIndicator()

    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      Auth_callback_queue.add(() => {
        getLastContactInfoSyncPromise()
        .then(() => this.onFollowClick(follow_type))
        .done()
      }, "event:create_building_following")

      Auth_action.showSignUpModal({
        enable_account_already_exists_error_message: false,
      })
      .then(({$modal, authentication_box}) => {
        authentication_box.on("sign_up:fail:account_conflict", ({email_address}) => {
          $modal.modal("hide")
          // Retry AFTER setting local contact email
          setContactInfoData({
            contact_email_address: email_address,
          })
          this.onFollowClick(follow_type)
        })
      })
      .done()
      return
    }

    let subscribe_building_following_inputs = {}
    Promise.resolve()
    .then(() => {
      Modal.showLoadingIndicator()

      if (isSignedIn()) {
        return Promise.resolve()
      }

      return create_user({
        authentication_handle:  local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        email_address:          local_contact_info_data.contact_email_address || "",
        sign_in_on_success:     true,
      })
      .then(({result_type}) => {
        switch (result_type) {
          case CreateUserResultTypes.SUCCESS:
            trackSignUp()
            refreshAndUpdateComponentPromise().done()
            return
          case CreateUserResultTypes.CONFLICT:
            return
          default:
            captureMessage(
              `unexpected/unhandled result_type: <${result_type}>`,
              {
                source_file_path:
                  "app/assets/javascripts/concepts/listings/contact/button/events/index",
              }
            )
            throw RejectionReasons.ERROR_REPORTED
        }
      })
    })
    .then(() => {
      const inputs = {
        // Need `||` to make flow happy
        user_authentication_handle:
          local_contact_info_data.user_authentication_handle ||
          local_contact_info_data.contact_email_address ||
          "",

        country_name_in_en: this.country_name_in_en,
        follow_type:        SubscribeCountryUFollowingFollowTypes.OVERSEAS,
      }
      subscribe_building_following_inputs = inputs
      return subscribe_country_user_following(inputs)
    })
    .then(({result_type}) => {
      switch (result_type) {
        case SubscribeCountryUFollowingResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from subscribe_building_following: <${result_type}>`,
            Object.assign({
              "source_file_path":
                SOURCE_FILE_PATH,
              "local_contact_info_data.contact_email_address":
                local_contact_info_data.contact_email_address || "unknown",
            }, subscribe_building_following_inputs),
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      Modal.hideLoadingIndicator()
      this.displayAsFollowing()
      this.runOnFollowedCallbacks()
    })
    .catch((whatever) => {
      Modal.hideLoadingIndicator()
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }

  private onUnfollowClick(_follow_type: string) {
    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      // Do nothing but just pretend to have unfollow
      return
    }

    Promise.resolve()
    .then(() => {
      return unsubscribe_country_user_following({
        // Need `||` to make flow happy
        user_authentication_handle:
          local_contact_info_data.user_authentication_handle ||
          local_contact_info_data.contact_email_address ||
          "",

        country_name_in_en: this.country_name_in_en,
        follow_type:        UnsubscribeCountryUFollowingFollowTypes.OVERSEAS,
      })
    })
    .then(({result_type}) => {
      switch (result_type) {
        case UnsubscribeCountryUFollowingResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from unsubscribe_building_following: <${result_type}>`,
            {
              source_file_path:
              SOURCE_FILE_PATH,
            }
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      this.displayAsNotFollowing()
    })
    .catch((whatever) => {
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }

  private displayAsFollowing() {
    this.$container
    .addClass("following")

    this.emit("country_following")
  }
  private displayAsNotFollowing() {
    this.$container
    .removeClass("following")
  }
}

const Factory = {
  _instance: null as null | FollowButton,
  instance(selector: InputSelector){
    if (this._instance !== null) { return this._instance }

    this._instance = this.create(selector)
    return this._instance
  },
  create(selector: InputSelector){
    return new FollowButton(selector)
  },
}

export {
  Factory,
}
